import api from "../../api.service"
import { getPaginatedResponse } from "../utils"
import { decompress, decrypt, compress, encrypt } from "../../../utils/cache-utils"
import { addDbItem, getDbItem } from "../../../utils/idb-util"
import { paginateAll } from "../../../services/api/utils"
import queryString from "query-string"
import { getEncryptionKey } from "../../../utils/key-utils"
import uniqBy from "lodash/uniqBy"

import { getRefId, getObjectId } from "../../../utils/utils"

import { subMinutes, endOfWeek } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { getTimeUtc } from "../../../utils/dates"

/**
 * For query parameters, see:
 * https://docs.api.missionx.ai/operation/operation-get-timesheets
 *
 * If you want to scope by a specific org, pass the "org" query param.
 */
export function getMyTimesheets(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/timesheets/my?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * For query parameters, see:
 * https://docs.api.missionx.ai/operation/operation-get-timesheets.
 */
export function getMyOrgTimesheets(orgId, queryParams = {}, signal) {
    return getMyTimesheets(
        {
            ...queryParams,
            org: orgId,
        },
        signal,
    )
}

/**
 * For query parameters, see:
 * https://docs.api.missionx.ai/operation/operation-get-timesheets
 */
export function getPmTimesheets(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/timesheets/pm?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}
export function getPmTimesheetSummary(queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/timesheets/pm-custom/summary-list?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * For query parameters, see:
 * https://docs.api.missionx.ai/operation/operation-get-timesheets
 */
export function getOrgTimesheetsCompanyLite(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/company/lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgTimesheetsCompanyUltraLite(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/company/ultra-lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgTimesheetsReportUltraLite(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/org-report-ultra-lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgTimesheetsProjectUltraLite(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/project/ultra-lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}
/**
 * For query parameters, see:
 * https://docs.api.missionx.ai/operation/operation-get-timesheets
 */
export function getOrgTimesheetsMissionLite(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/project/lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}
export function getOrgTimesheetsUltraLite(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/org-ultra-lite?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgApprovedTimesheetsUltraLean(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/project/lean-approved-time-by-project?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgApprovedTimesheetsUtilization(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/project/approved-utilization?${qs}`, { signal })
        .then((res) =>
            getPaginatedResponse({
                ...res.data,
                value: res.data.value.map((t) => {
                    return {
                        ...t,
                        status: "approved",
                        belongsToOrg: orgId,
                        type: "project",
                        weekEnd: getTimeUtc(endOfWeek(toZonedTime(t.weekStart), { weekStartsOn: 1 })),
                    }
                }),
            }),
        )
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgServerApprovedTimesheetsBudget(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets/project/approved-budget?${qs}`, { signal })
        .then((res) =>
            getPaginatedResponse({
                ...res.data,
                value: res.data.value.map((t) => {
                    return {
                        ...t,
                        status: "approved",
                        belongsToOrg: orgId,
                        type: "project",
                        weekEnd: getTimeUtc(endOfWeek(toZonedTime(t.weekStart), { weekStartsOn: 1 })),
                        planItemId: t.planItemId || getObjectId(t.snapshot?.planItem),
                    }
                }),
            }),
        )
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

export function getOrgTimesheets(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/timesheets?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            if (err.code === "ERR_CANCELED") {
                console.log("Request was canceled")
                return // or throw if you want caller to handle it
            }
            throw api.getResponseError(err)
        })
}

export async function getMissionApprovedTimesheets({ app, mission, from, to }) {
    const encryptionKey = await getEncryptionKey({ app })

    const tsDB = await getDbItem("missionTimesheets", "store", mission._id)

    const meInMission = mission.people.find((p) => getRefId(p) === app.state.person._id && p.permission < 3)

    const cachedTimesheets = []

    if (tsDB?.timesheets) {
        const decryptedSheets = await decrypt(encryptionKey, tsDB.timesheets)

        const cts = decompress(decryptedSheets) || []

        cachedTimesheets.push(...cts)
    }

    const paramsA = {
        limit: 100,
        type: "project",
        project: mission?._id,
        status: ["approved"],
        expand: ["snapshot"],
        exclude: ["planItem"],
        select: [
            "_id",
            "person",
            "mission",
            "snapshot.planItem._id",
            "snapshot.planItem.rate",
            "snapshot.planItem.rateTime",
            "snapshot.planItem.billRate",
            "snapshot.planItem.billUnit",
            "snapshot.planItem.role",
            "snapshot.planItem.startDate",
            "snapshot.planItem.endDate",
            "snapshot.planItem.missionId",
            "snapshot.planItem.raciOnTasks",
            "weekStart",
            "weekEnd",
            "actionItems",
            "planItem",
        ],
        updatedAt: tsDB?.updatedAt ? `gte ${tsDB.updatedAt}` : undefined,
        exists: "actionItems.actionItemId:true",
    }

    const paramsB = {
        limit: 100,
        type: "project",
        status: ["submitted", "rejected"],
        select: ["_id"],
        updatedAt: tsDB?.updatedAt ? `gte ${tsDB.updatedAt}` : undefined,
        project: mission?._id,
    }

    let res = []
    let notApprovedTs = []
    try {
        const isPm = meInMission?.permission === 2 || meInMission?.isProjectManager
        const orgData = app.state.orgs.find((o) => getObjectId(o) === getObjectId(mission.org))
        const meInOrg = orgData.people.find((p) => getRefId(p) === app.state.person._id)

        if (isPm || meInOrg?.permission === 1 || meInOrg?.permission === 2) {
            res = await paginateAll(
                isPm ? getPmTimesheets(paramsA) : getOrgTimesheets(getObjectId(mission.org), paramsA),
            )

            notApprovedTs = await paginateAll(
                isPm ? getPmTimesheets(paramsB) : getOrgTimesheets(getObjectId(mission.org), paramsB),
            )
        }
    } catch (e) {}

    const updatedTimesheets = uniqBy([...(res || []), ...(cachedTimesheets || [])], "_id").filter(
        (n) => !notApprovedTs.some((x) => x._id === n._id),
    )

    const compressedTimesheets = compress(updatedTimesheets)
    const encryptedTimesheets = await encrypt(encryptionKey, compressedTimesheets)

    addDbItem("missionTimesheets", "store", {
        id: mission._id,
        timesheets: encryptedTimesheets,
        updatedAt: subMinutes(new Date(), 1).toISOString(),
    })

    return updatedTimesheets.map((t) => ({ ...t, planItemId: t.planItem, status: "approved" }))
}
